import { useEffect, useCallback } from 'react';

export const usePerformance = () => {
  // Optimización de scroll con throttling
  const throttle = useCallback((func, limit) => {
    let inThrottle;
    return function() {
      const args = arguments;
      const context = this;
      if (!inThrottle) {
        func.apply(context, args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    }
  }, []);

  // Optimización de resize con debouncing
  const debounce = useCallback((func, wait) => {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }, []);

  // Preload de imágenes críticas
  const preloadImage = useCallback((src) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = src;
    });
  }, []);

  // Preload de múltiples imágenes
  const preloadImages = useCallback((imageUrls) => {
    return Promise.all(imageUrls.map(url => preloadImage(url)));
  }, [preloadImage]);

  // Optimización de animaciones con requestAnimationFrame
  const smoothScroll = useCallback((target, duration = 1000) => {
    // Deferir la lectura de la posición para evitar reprocesamiento forzado
    requestAnimationFrame(() => {
      const targetPosition = target.getBoundingClientRect().top;
      const startPosition = window.pageYOffset;
      const distance = targetPosition - startPosition;
      let startTime = null;

      function animation(currentTime) {
        if (startTime === null) startTime = currentTime;
        const timeElapsed = currentTime - startTime;
        const run = ease(timeElapsed, startPosition, distance, duration);
        window.scrollTo(0, run);
        if (timeElapsed < duration) requestAnimationFrame(animation);
      }

      function ease(t, b, c, d) {
        t /= d / 2;
        if (t < 1) return c / 2 * t * t + b;
        t--;
        return -c / 2 * (t * (t - 2) - 1) + b;
      }

      requestAnimationFrame(animation);
    });
  }, []);

  // Optimización de eventos de scroll
  useEffect(() => {
    const handleScroll = throttle(() => {
      // Aquí puedes agregar lógica de optimización de scroll
    }, 16); // ~60fps

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [throttle]);

  // Optimización de eventos de resize
  useEffect(() => {
    const handleResize = debounce(() => {
      // Aquí puedes agregar lógica de optimización de resize
    }, 250);

    window.addEventListener('resize', handleResize, { passive: true });
    return () => window.removeEventListener('resize', handleResize);
  }, [debounce]);

  return {
    throttle,
    debounce,
    preloadImage,
    preloadImages,
    smoothScroll
  };
}; 